import React from "react"
import {Paper, Box, Typography, Divider, Chip} from "@mui/material"

import {OfferTierCard} from "./OfferTierCard"
import {RemoveOfferButton} from "./RemoveOfferButton"

import {CustomerOffer} from "@indebted/api"

type OfferCardProps = {
	offer: CustomerOffer
}

function OfferCard({offer}: OfferCardProps) {
	const formatDate = (dateString: string) => {
		if (!dateString) return ""
		const date = new Date(dateString)
		return date.toLocaleDateString()
	}

	const isOfferUpcoming = () => {
		const today = new Date()
		today.setHours(0, 0, 0, 0)
		const startDate = new Date(offer.StartDate)
		startDate.setHours(0, 0, 0, 0)
		return today < startDate
	}

	const isOfferExpired = () => {
		const today = new Date()
		today.setHours(0, 0, 0, 0)
		const endDate = new Date(offer.EndDate)
		endDate.setHours(0, 0, 0, 0)
		// Offers are valid through the END of their end date, so only expire the day AFTER
		return today > endDate
	}

	const getOfferBadgeConfig = () => {
		const isActive = offer.Status.toLowerCase() === "active"
		if (!isActive) {
			return {
				label: offer.Status.toUpperCase(),
				backgroundColor: "#e0e0e0",
				color: "#666",
				borderColor: "#e0e0e0",
			}
		}

		if (isOfferExpired()) {
			return {
				label: "EXPIRED",
				backgroundColor: "#e0e0e0",
				color: "#666",
				borderColor: "#e0e0e0",
			}
		}

		const upcoming = isOfferUpcoming()
		return {
			label: upcoming ? "UPCOMING" : "AVAILABLE",
			backgroundColor: upcoming ? "#ff9800" : "#4caf50",
			color: "white",
			borderColor: upcoming ? "#ff9800" : "#4caf50",
		}
	}

	const badgeConfig = getOfferBadgeConfig()

	const sortedTiers = React.useMemo(
		() =>
			[...offer.Tiers].sort((a, b) => {
				if (a.TierType === "ONE_OFF" && b.TierType !== "ONE_OFF") return -1
				if (a.TierType !== "ONE_OFF" && b.TierType === "ONE_OFF") return 1
				return (a.MinimumNumberOfPayments ?? 0) - (b.MinimumNumberOfPayments ?? 0)
			}),
		[offer.Tiers],
	)

	return (
		<Paper
			variant="outlined"
			style={{
				padding: "16px",
				borderLeft: `4px solid ${badgeConfig.borderColor}`,
			}}
		>
			<Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
				<Box>
					<Box display="flex" alignItems="flex-start" style={{gap: "8px"}} mb={1}>
						<Typography variant="h6">
							Offer available to customer from {formatDate(offer.StartDate)} to{" "}
							{formatDate(offer.EndDate)}
						</Typography>
						<Chip
							label={badgeConfig.label}
							size="small"
							style={{
								backgroundColor: badgeConfig.backgroundColor,
								color: badgeConfig.color,
								height: "20px",
								fontSize: "0.7rem",
							}}
						/>
					</Box>
					<Typography variant="body2" color="textSecondary">
						Offer ID: {offer.OfferID}
					</Typography>
				</Box>
				{offer.Status.toLowerCase() === "active" && <RemoveOfferButton offerID={offer.OfferID} />}
			</Box>

			{offer.CampaignID && (
				<Box mb={2}>
					<Typography variant="body2" color="textSecondary">
						Campaign ID: {offer.CampaignID}
					</Typography>
				</Box>
			)}

			{offer.Notes && (
				<Box mb={2}>
					<Typography variant="body2" color="textSecondary">
						Notes: {offer.Notes}
					</Typography>
				</Box>
			)}

			<Divider style={{margin: "16px 0"}} />

			<Typography variant="subtitle2" gutterBottom>
				Options
			</Typography>
			<Box display="flex" flexDirection="column" style={{gap: "12px"}}>
				{sortedTiers.map((tier, index) => (
					<OfferTierCard key={tier.TierID} tier={tier} index={index} />
				))}
			</Box>

			<Box mt={2}>
				<Typography variant="caption" color="textSecondary">
					Created: {formatDate(offer.CreatedAt)}
					{offer.AgentEmail && ` by ${offer.AgentEmail}`}
				</Typography>
			</Box>
		</Paper>
	)
}

export {OfferCard}
